import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Star, Sparkle, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Reviews() {
  const { activeBiz } = useOutletContext();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [drafting, setDrafting] = useState({});

  useEffect(() => {
    if (!activeBiz) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/businesses/${activeBiz.id}/reviews`);
        setReviews(data.reviews || []);
      } catch {}
      setLoading(false);
    })();
  }, [activeBiz]);

  const draftReply = async (rev) => {
    setDrafting({ ...drafting, [rev.id]: true });
    try {
      const { data } = await api.post(`/businesses/${activeBiz.id}/ai/review-reply`, {
        review_text: rev.text,
        rating: rev.rating,
      });
      setDrafts({ ...drafts, [rev.id]: data.reply });
    } catch (e) { toast.error(formatErr(e)); }
    setDrafting({ ...drafting, [rev.id]: false });
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Reply copied"); };

  if (!activeBiz) return <div className="p-12 text-zinc-500">Add a business first.</div>;

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-5xl">
      <header>
        <div className="label-tiny mb-1">Reviews</div>
        <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="reviews-title">Review Reply Studio</h1>
        <p className="text-zinc-500 mt-1">AI-drafted replies in your voice. Read-only mode: copy and paste to GBP.</p>
      </header>

      {loading ? (
        <div className="py-16 flex justify-center"><span className="spinner text-zinc-400"></span></div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} className="bento p-5" data-testid={`review-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{r.author}</span>
                    <span className="flex">{Array.from({length: 5}).map((_, i) => (
                      <Star key={i} size={14} weight={i < r.rating ? "fill" : "regular"} className={i < r.rating ? "text-[#EAB308]" : "text-zinc-300"} />
                    ))}</span>
                  </div>
                  <p className="text-zinc-700 mt-2">{r.text}</p>
                </div>
                <span className="text-xs text-zinc-400 font-mono whitespace-nowrap">{new Date(r.date).toLocaleDateString()}</span>
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-100">
                {drafts[r.id] ? (
                  <div className="space-y-3">
                    <div className="label-tiny">AI Draft</div>
                    <div className="bg-zinc-50 p-4 font-mono text-sm leading-relaxed border-l-2 border-[#002FA7]" data-testid={`draft-${r.id}`}>{drafts[r.id]}</div>
                    <button className="btn-secondary !py-2 !text-[11px]" onClick={() => copy(drafts[r.id])} data-testid={`copy-reply-${r.id}`}>
                      <Copy size={12} weight="bold" /> Copy reply
                    </button>
                  </div>
                ) : (
                  <button className="btn-primary !py-2 !text-[11px]" onClick={() => draftReply(r)} disabled={drafting[r.id]} data-testid={`draft-reply-${r.id}`}>
                    {drafting[r.id] ? <span className="spinner"></span> : <><Sparkle size={12} weight="fill" /> Draft AI Reply</>}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
